// Settings › Profile and Preferences (Console D, views/settings.js `#/settings/profile` and
// `#/settings/preferences`; APS-320): your name (the email comes from the sign-in) and your
// organizations (the current one's plan and retention are on General, APS-319); then the theme on this device, the
// language the console and Apsio's emails use (D63, saved on the profile) and the answer about
// analytics in the console (plan step 2.4, only on builds that have it). Profile also lists your
// signed-in CLIs and signs one out (APS-486).
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import {
  cancelOwnErasure,
  cliSessionsQuery,
  membersQuery,
  meQuery,
  orgQuery,
  ownErasureQuery,
  scheduleOwnErasure,
  useSignOutCli,
  useUpdateProfile,
} from '../api/queries'
import { privacyUrl } from '../components/Analytics'
import { Confirm } from '../components/Confirm'
import { Btn, btnClass, Kbd, PageHead, Section, Seg, Status } from '../components/d'
import { LOCALE_NAMES } from '../components/shell/AccountMenu'
import { isLocale, locales, useI18n } from '../i18n'
import { consoleAnalytics } from '../lib/analytics'
import { day, initials } from '../lib/format'
import { findKey } from '../lib/keys'
import { leave } from '../lib/leave'
import { readTheme, saveTheme, type Theme } from '../lib/theme'
import { leaveSandbox } from '../sandbox'
import { useProjectParams } from './params'
import { devSignInBuild, hostedSignInUrl } from './SignIn'

const ROLE_KEY = { owner: 'role.owner', admin: 'role.admin', member: 'role.member' } as const

/** A zone's offset now, as the mock writes it: "UTC−3", "UTC+5:30", "UTC". */
export function zoneOffset(zone: string, now: Date = new Date()): string {
  const name =
    new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName: 'shortOffset' })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value ?? 'GMT'
  // "GMT" in browsers, "GMT+0" in some runtimes: both are UTC.
  return name
    .replace(/^GMT(\+0)?$/, 'UTC')
    .replace(/^GMT/, 'UTC')
    .replace('-', '−')
}

/** UTC first, then every zone the browser knows, each with its offset now (APS-398). */
function zoneOptions(saved: string | null | undefined): { value: string; label: string }[] {
  const zones = ['UTC', ...Intl.supportedValuesOf('timeZone').filter((z) => z !== 'UTC')]
  // A saved zone this browser does not list still shows as chosen.
  if (saved && !zones.includes(saved)) zones.unshift(saved)
  const now = new Date()
  return zones.map((z) => {
    try {
      return { value: z, label: `${z} (${zoneOffset(z, now)})` }
    } catch {
      return { value: z, label: z }
    }
  })
}

/** A saved change, or why it was not saved, as a status line. */
function Saved({ update }: { update: ReturnType<typeof useUpdateProfile> }) {
  const { t } = useI18n()
  if (update.isSuccess)
    return (
      <p role="status" className="se-hint">
        <Status s="ok" label={t('settings.profile.saved')} />
      </p>
    )
  if (!update.error) return null
  return (
    <p role="alert" className="se-hint">
      <Status
        s="miss"
        label={isApiError(update.error, 429) ? t('settings.rateLimited') : t('state.failed')}
      />
    </p>
  )
}

export function ProfilePage({
  orgId,
  crumb,
  title,
}: {
  orgId: string
  crumb: string
  title: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const me = useQuery(meQuery)
  const org = useQuery(orgQuery(orgId))
  const members = useQuery(membersQuery(orgId))
  const update = useUpdateProfile()
  const [name, setName] = useState('')
  useEffect(() => {
    if (me.data) setName(me.data.user.name)
  }, [me.data])
  const ids = { name: useId(), email: useId(), emailHint: useId() }
  const user = me.data?.user
  const since = members.data?.find((m) => m.user_id === user?.id)?.created_at
  return (
    <main id="main" className="pg se-pg profile">
      <PageHead
        crumb={crumb}
        title={title}
        meta={user ? <span translate="no">{user.email}</span> : undefined}
      />
      <div className="grid2">
        <div className="sections">
          <Section
            title={t('settings.profile')}
            meta={org.data ? t('settings.profile.seenBy', { org: org.data.name }) : undefined}
          >
            {user ? (
              <div className="se-me">
                <span className="avatar" aria-hidden="true">
                  {initials(user.name, user.email)}
                </span>
                <span>
                  <b>{user.name || user.email}</b>
                  {org.data ? (
                    <span>
                      {[
                        t(ROLE_KEY[org.data.role]),
                        org.data.name,
                        since ? t('settings.profile.memberSince', { date: day(i18n, since) }) : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  ) : null}
                </span>
              </div>
            ) : null}
            <form
              className="se-sp"
              onSubmit={(e) => {
                e.preventDefault()
                update.mutate({ name: name.trim() })
              }}
            >
              <label className="lbl" htmlFor={ids.name}>
                {t('settings.profile.name')}
                <input
                  id={ids.name}
                  className="input narrow"
                  maxLength={100}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <div className="lbl">
                <label htmlFor={ids.email}>{t('settings.profile.email')}</label>
                <input
                  id={ids.email}
                  className="input mono narrow"
                  value={user?.email ?? ''}
                  disabled
                  aria-describedby={ids.emailHint}
                  translate="no"
                />
                <span id={ids.emailHint} className="se-hint">
                  {t('settings.profile.emailHint')}
                </span>
              </div>
              <div>
                <Btn type="submit" variant="solid" disabled={update.isPending}>
                  {t('settings.profile.save')}
                </Btn>
              </div>
              <Saved update={update} />
            </form>
          </Section>
        </div>
        <div className="sections">
          {me.data ? (
            <Section title={t('settings.profile.orgs')} meta={String(me.data.orgs.length)} minor>
              <ul className="rl">
                {me.data.orgs.map((o) => (
                  <li key={o.id}>
                    <span>
                      {o.id === orgId ? (
                        <>
                          <b>{o.name}</b>{' '}
                          <span className="faint">· {t('settings.profile.current')}</span>
                        </>
                      ) : (
                        <Link className="ul" to="/o/$orgId" params={{ orgId: o.id }}>
                          {o.name}
                        </Link>
                      )}
                    </span>
                    <span>{t(ROLE_KEY[o.role]).toLowerCase()}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          <SignedInClis />
          {user ? (
            <DeleteAccount orgId={orgId} email={user.email} orgCount={me.data?.orgs.length ?? 0} />
          ) : null}
        </div>
      </div>
    </main>
  )
}

/**
 * Your signed-in CLIs (APS-486): each `apsio login` still signed in, with what the CLI said it was
 * (self-reported, as text; an unknown one was recorded at its first read, signed in before the API
 * recorded sign-ins), when it signed in and when it was last used; signing one out (a lost
 * laptop, a forgotten machine) stops its tokens at once, and its next command asks to sign in.
 * Not on the sandbox, which has no account.
 */
function SignedInClis() {
  const i18n = useI18n()
  const { t } = i18n
  const sessions = useQuery(cliSessionsQuery)
  const signOut = useSignOutCli()
  if (sessions.isPending || sessions.isError) return null
  const list = sessions.data
  return (
    <Section title={t('settings.cli.title')} meta={String(list.length)} minor>
      {list.length === 0 ? (
        <p className="se-hint">{t('settings.cli.none')}</p>
      ) : (
        <ul className="rl se-cli">
          {list.map((s) => {
            const name = s.label || t('settings.cli.unnamed')
            return (
              <li key={s.id}>
                <span>
                  {/* What the CLI said is data (mono, as text); our words for one that said nothing
                      are a sentence, in the regular face and translated (Mosaic). */}
                  {s.label ? (
                    <b className="mono" translate="no">
                      {s.label}
                    </b>
                  ) : (
                    <b className="se-cli-unknown">{name}</b>
                  )}
                  <span className="t2">
                    {t('settings.cli.when', {
                      date: day(i18n, s.started_at),
                      used: i18n.relative(s.last_used_at),
                    })}
                  </span>
                </span>
                <span>
                  <Confirm
                    buttonClass={btnClass('danger', 'sm')}
                    label={t('settings.cli.signOut')}
                    buttonName={t('settings.cli.signOutName', { name })}
                    title={t('settings.cli.signOutTitle')}
                    body={t('settings.cli.signOutBody', { name })}
                    confirm={t('settings.cli.signOut')}
                    // It cuts a device's access, as revoking a token does (Mosaic).
                    confirmClass="btn danger solid"
                    busy={signOut.isPending}
                    onConfirm={() => signOut.mutate(s.id)}
                  />
                </span>
              </li>
            )
          })}
        </ul>
      )}
      <p className="se-hint">{t('settings.cli.hint')}</p>
      {signOut.error ? (
        <p role="alert" className="notice error">
          {t('state.failed')}
        </p>
      ) : null}
    </Section>
  )
}

/**
 * Deleting your own account (APS-402; LGPD and GDPR), at the foot of Profile as the mock's
 * danger block: refused while you are the only owner of an organization others belong to (named,
 * with Members for the current one); otherwise your email typed in a dialog, a recent sign-in
 * (else the sign-in page asks for the credentials again and brings you back), then it is
 * scheduled and you are signed out. Signed in again within the grace, it says when, and you can
 * keep your account; one Apsio's support scheduled is support's to stop.
 */
function DeleteAccount({
  orgId,
  email,
  orgCount,
}: {
  orgId: string
  email: string
  orgCount: number
}) {
  const i18n = useI18n()
  const { t } = i18n
  const qc = useQueryClient()
  const { projectId } = useProjectParams()
  const state = useQuery({ ...ownErasureQuery, throwOnError: false })
  const dialog = useRef<HTMLDialogElement>(null)
  const ids = { title: useId(), confirm: useId(), hint: useId(), reason: useId() }
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  // Not set up on this server (503): nothing to offer.
  if (!state.data) return null
  const { erasure, blockers, deleted } = state.data
  // The shared day, as a project's deletion says it (APS-401): "Oct 21".
  const when = (iso: string) => day(i18n, iso)

  if (erasure)
    return (
      <div className="se-danger">
        <div>
          <b>{t('settings.profile.delete.scheduledTitle')}</b>
          <p>
            {erasure.by_support
              ? t('settings.profile.delete.bySupport', { date: when(erasure.execute_after) })
              : t('settings.profile.delete.scheduled', { date: when(erasure.execute_after) })}
          </p>
          {error ? (
            <p role="alert">
              <Status s="miss" label={error} />
            </p>
          ) : null}
        </div>
        {erasure.by_support || erasure.status !== 'scheduled' ? null : (
          <Btn
            className="se-keep"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              setError(undefined)
              try {
                await cancelOwnErasure()
                await qc.invalidateQueries({ queryKey: ['me'] })
              } catch {
                setError(t('state.failed'))
              } finally {
                setBusy(false)
              }
            }}
          >
            {t('settings.profile.delete.keep')}
          </Btn>
        )}
      </div>
    )

  const kept = orgCount - deleted.length
  const names = (list: { name: string }[]) => list.map((o) => o.name).join(', ')
  const matches = typed.trim().toLowerCase() === email.toLowerCase()

  async function submit() {
    setBusy(true)
    setError(undefined)
    try {
      const done = await scheduleOwnErasure(typed.trim())
      qc.clear()
      // The sandbox's token goes with the session (D105), as at sign-out.
      leaveSandbox()
      leave(done.sign_out_url ?? '/sign-in')
    } catch (e) {
      // An old sign-in: sign in again, then back here to confirm.
      if (isApiError(e, 401) && e.code === 'reauth_required') {
        const back = window.location.pathname + window.location.search
        leave(
          devSignInBuild
            ? `/sign-in?return_to=${encodeURIComponent(back)}`
            : hostedSignInUrl('sign-in', back, true),
        )
        return
      }
      setError(
        isApiError(e) && (e.status === 409 || e.status === 400) ? e.message : t('state.failed'),
      )
      setBusy(false)
    }
  }

  return (
    <>
      <div className="se-danger">
        <div>
          <b>{t('settings.profile.delete.title')}</b>
          {blockers.length ? (
            <p id={ids.reason}>
              {t('settings.profile.delete.blocked', {
                orgs: names(blockers),
                count: blockers.length,
              })}{' '}
              {blockers.some((b) => b.org_id === orgId) ? (
                <Link
                  className="ul"
                  to="/o/$orgId/p/$projectId/settings/$section"
                  params={{ orgId, projectId, section: 'members' }}
                >
                  {t('settings.tab.members')}
                </Link>
              ) : null}
            </p>
          ) : (
            <p>
              {kept > 0 ? t('settings.profile.delete.leave', { count: kept }) : null}
              {deleted.length
                ? ` ${t('settings.profile.delete.goes', { orgs: names(deleted), count: deleted.length })}`
                : null}
            </p>
          )}
        </div>
        {/* Blocked: still reachable by keyboard and screen readers, which hear why (Mosaic). */}
        <Btn
          aria-disabled={blockers.length > 0 ? true : undefined}
          aria-describedby={blockers.length > 0 ? ids.reason : undefined}
          onClick={() => {
            if (blockers.length) return
            setTyped('')
            setError(undefined)
            dialog.current?.showModal()
          }}
        >
          {t('settings.profile.delete.button')}
        </Btn>
      </div>
      <dialog ref={dialog} aria-labelledby={ids.title}>
        <form
          className="stack"
          style={{ gap: 14 }}
          onSubmit={(e) => {
            e.preventDefault()
            if (matches && !busy) void submit()
          }}
        >
          <h2 id={ids.title} style={{ fontSize: 17 }}>
            {t('settings.profile.delete.dialogTitle')}
          </h2>
          <p className="dim">{t('settings.profile.delete.dialogBody')}</p>
          {deleted.length ? (
            <p className="dim">
              {t('settings.profile.delete.goes', { orgs: names(deleted), count: deleted.length })}
            </p>
          ) : null}
          <label className="lbl" htmlFor={ids.confirm}>
            {t('settings.profile.delete.typeEmail')}
            <input
              id={ids.confirm}
              className="input mono"
              type="email"
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              aria-describedby={ids.hint}
              translate="no"
            />
          </label>
          <span id={ids.hint} className="se-hint" translate="no">
            {email}
          </span>
          {error ? (
            <p role="alert">
              <Status s="miss" label={error} />
            </p>
          ) : null}
          <div className="head-actions">
            {/* The same danger button as deleting a project (APS-401): irreversible, alike. */}
            <button type="submit" className="btn danger" disabled={!matches || busy}>
              {t('settings.profile.delete.confirm')}
            </button>
            <button type="button" className="btn" onClick={() => dialog.current?.close()}>
              {t('settings.cancel')}
            </button>
          </div>
        </form>
      </dialog>
    </>
  )
}

export function PreferencesPage({ crumb, title }: { crumb: string; title: string }) {
  const { t, locale } = useI18n()
  const me = useQuery(meQuery)
  const update = useUpdateProfile()
  const [theme, setTheme] = useState<Theme>(readTheme)
  const ids = { lang: useId(), langHint: useId(), zone: useId(), zoneHint: useId() }
  const saved = me.data?.user.time_zone
  const zones = useMemo(() => zoneOptions(saved), [saved])
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  // The analytics answer (plan step 2.4), only on builds that have PostHog.
  const analytics = consoleAnalytics()
  const consent = me.data?.user.analytics_consent ?? ''
  return (
    <main id="main" className="pg se-pg preferences">
      <PageHead crumb={crumb} title={title} meta={t('settings.prefs.meta')} />
      <div className="grid2">
        <div className="sections">
          <Section title={t('settings.prefs.appearance')}>
            <div className="se-sp">
              <div className="lbl">
                <span>{t('user.theme')}</span>
                <Seg
                  label={t('user.theme')}
                  value={theme}
                  onChange={(v) => {
                    setTheme(v)
                    saveTheme(v)
                  }}
                  options={[
                    { value: 'system', label: t('user.theme.systemShort') },
                    { value: 'light', label: t('user.theme.light') },
                    { value: 'dark', label: t('user.theme.dark') },
                  ]}
                />
                <span className="se-hint">{t('settings.prefs.themeHint')}</span>
              </div>
            </div>
          </Section>
          <Section title={t('settings.prefs.languageTime')}>
            <div className="se-sp">
              <div className="lbl">
                <label htmlFor={ids.lang}>{t('user.language')}</label>
                <select
                  id={ids.lang}
                  className="select narrow"
                  value={me.data?.user.locale ?? locale}
                  disabled={update.isPending}
                  aria-describedby={ids.langHint}
                  onChange={(e) => {
                    if (isLocale(e.target.value)) update.mutate({ locale: e.target.value })
                  }}
                >
                  {locales.map((l) => (
                    <option key={l} value={l} lang={l}>
                      {LOCALE_NAMES[l]}
                    </option>
                  ))}
                </select>
                <span id={ids.langHint} className="se-hint">
                  {t('settings.profile.languageHint')}
                </span>
              </div>
              <div className="lbl">
                <label htmlFor={ids.zone}>{t('settings.prefs.timeZone')}</label>
                <select
                  id={ids.zone}
                  className="select narrow"
                  value={saved ?? ''}
                  disabled={update.isPending || me.isPending}
                  aria-describedby={ids.zoneHint}
                  onChange={(e) => update.mutate({ time_zone: e.target.value || null })}
                >
                  <option value="">
                    {t('settings.prefs.timeZoneBrowser', {
                      zone: `${browserZone} (${zoneOffset(browserZone)})`,
                    })}
                  </option>
                  {zones.map((z) => (
                    <option key={z.value} value={z.value}>
                      {z.label}
                    </option>
                  ))}
                </select>
                <span id={ids.zoneHint} className="se-hint">
                  {t('settings.prefs.timeZoneHint')}
                </span>
              </div>
              <Saved update={update} />
            </div>
          </Section>
        </div>
        <div className="sections">
          {analytics ? (
            <Section title={t('settings.profile.analytics')}>
              <div className="se-sp">
                <Seg
                  label={t('settings.profile.analytics')}
                  value={consent}
                  onChange={(v) => {
                    if (v === 'granted' || v === 'denied') update.mutate({ analytics_consent: v })
                  }}
                  options={[
                    { value: 'granted', label: t('settings.prefs.allowed') },
                    { value: 'denied', label: t('settings.prefs.declined') },
                  ]}
                />
                {/* What each answer means, under the short choice (the mock's hint). Unanswered,
                    neither is pressed and the question's card is open, so the hint says no more. */}
                <p className="se-hint">
                  {t('settings.profile.analyticsGranted')}. {t('settings.profile.analyticsDenied')}.{' '}
                  {t('settings.profile.analyticsHint')}{' '}
                  <a className="ul" href={privacyUrl(locale)}>
                    {t('consent.privacy')}
                  </a>
                </p>
              </div>
            </Section>
          ) : null}
          {/* The keys most used, from the shell's list; ? opens all of them anywhere. */}
          <Section title={t('keys.title')} meta={t('settings.prefs.keysMeta')}>
            <ul className="se-keys">
              {(
                [
                  [t('settings.prefs.keysPalette'), findKey().split(' ')],
                  [t('keys.find'), ['/']],
                  [t('keys.rows'), ['j', 'k']],
                  [t('settings.prefs.keysAll'), ['?']],
                ] as const
              ).map(([label, keys]) => (
                <li key={label}>
                  <span>{label}</span>
                  <span translate="no">
                    {keys.map((k) => (
                      <Kbd key={k}>{k}</Kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </main>
  )
}
